import { useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import { groupUnitsForPicker } from '../units'
import { IngredientPicker } from './IngredientPicker'
import { IngredientCreateModal } from './IngredientCreateModal'
import { ModalShell } from './ModalShell'
import { ConfirmDialog } from './Dialog'
import type { IngredientRow, ShoppingItem } from '../types'

type Props = {
  open: boolean
  // When editing, ingredient is fixed.
  item: ShoppingItem | null
  onClose: () => void
}

export function ShoppingItemModal({ open, item, onClose }: Props) {
  const [ing, setIng] = useState<IngredientRow | null>(
    item
      ? {
          id: item.ingredient_id,
          slug: '',
          owned: item.ingredient_owned,
          name: item.ingredient_name,
          default_unit: item.unit,
        }
      : null,
  )
  const [quantity, setQuantity] = useState<string>(item ? String(item.quantity) : '')
  const [unit, setUnit] = useState<string>(item?.unit || 'ea')
  const [notes, setNotes] = useState<string>(item?.notes || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState<string | null>(null)
  const [confirmDel, setConfirmDel] = useState(false)

  const grouped = groupUnitsForPicker()

  const save = async () => {
    setError(null)
    const qty = Number(quantity)
    if (!ing && !item) { setError('Pick an ingredient.'); return }
    if (!qty || qty <= 0) { setError('Quantity must be > 0.'); return }
    setSaving(true)
    try {
      if (item) {
        await api.updateShoppingItem(item.id, {
          quantity: qty, unit, notes: notes || undefined,
        })
      } else if (ing) {
        await api.createShoppingItem({
          ingredient_id: ing.id, quantity: qty, unit,
          notes: notes || undefined,
        })
      }
      onClose()
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  const reallyDel = async () => {
    if (!item) return
    setConfirmDel(false)
    setSaving(true)
    try {
      await api.deleteShoppingItem(item.id)
      onClose()
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <ModalShell
        open={open}
        onClose={onClose}
        zIndex={60}
        autoHeight
        panelStyle={{ width: 'min(420px, 100%)', padding: '18px 20px', gap: 12 }}
      >
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>{item ? 'Edit shopping item' : 'New shopping item'}</div>
          <div style={{
            fontFamily: theme.serif, fontSize: 22, fontWeight: theme.headWeight,
            color: theme.ink, marginTop: 2,
          }}>{ing?.name ?? 'Pick an ingredient'}</div>
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: theme.radius,
            background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
            fontFamily: theme.sans, fontSize: 12,
          }}>{error}</div>
        )}

        {!item && (
          <Field label="Ingredient">
            <IngredientPicker
              value={ing ? { ingredient_id: ing.id, name: ing.name, default_unit: ing.default_unit } : null}
              onChange={(row: IngredientRow) => {
                setIng(row)
                if (row.default_unit) setUnit(row.default_unit)
                else if (row.purchase_unit) setUnit(row.purchase_unit)
              }}
              onCreateRequest={(seed) => setCreating(seed)}
            />
          </Field>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Quantity">
            <input type="number" inputMode="decimal" min="0" step="any"
              value={quantity} onChange={e => setQuantity(e.target.value)}
              style={inputStyle}/>
          </Field>
          <Field label="Unit">
            <select value={unit} onChange={e => setUnit(e.target.value)}
              style={{ ...inputStyle, appearance: 'auto' }}>
              <optgroup label="Mass">{grouped.mass.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}</optgroup>
              <optgroup label="Volume">{grouped.volume.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}</optgroup>
              <optgroup label="Count">{grouped.count.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}</optgroup>
            </select>
          </Field>
        </div>

        <Field label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)} style={inputStyle}/>
        </Field>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 4 }}>
          <div>
            {item && (
              <button onClick={() => setConfirmDel(true)} disabled={saving} style={btnDanger}>Delete</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={btnSecondary}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </ModalShell>

      <ConfirmDialog
        open={confirmDel}
        message="Remove from shopping list?"
        confirmLabel="Remove"
        danger
        onConfirm={reallyDel}
        onCancel={() => setConfirmDel(false)}
      />

      <IngredientCreateModal
        open={creating !== null} seedName={creating ?? ''}
        onCancel={() => setCreating(null)}
        onCreated={(row: IngredientRow) => {
          setIng(row)
          if (row.default_unit) setUnit(row.default_unit)
          setCreating(null)
        }}
      />
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>{label}</span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  all: 'unset', boxSizing: 'border-box', width: '100%',
  padding: '7px 10px', borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`, background: theme.bg,
  fontFamily: theme.sans, fontSize: 13, color: theme.ink,
}
const btnSecondary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink,
  border: `1px solid ${theme.lineStrong}`,
}
const btnPrimary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface, background: theme.ink,
}
const btnDanger: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: 'oklch(0.42 0.16 28)',
  border: `1px solid oklch(0.42 0.16 28 / 0.45)`,
}
